import axios from 'axios';
import http from './http';

export type NoteDto = {
  externalId: string;
  content: string;
  createdById: number;
  createdByRole: string;
  lastEditedById: number | null;
  lastEditedByRole: string | null;
  createdAt: string;
  updatedAt: string;
};

export type TodoDto = {
  externalId: string;
  assignedToRole: 'counselor' | 'student' | string;
  description: string;
  isCompleted: boolean;
  completedAt: string | null;
  readAt: string | null;
  createdById: number;
  createdByRole: string;
  createdAt: string;
  updatedAt: string;
};

export type NotificationDto = {
  id: number;
  userId: number;
  type: string;
  title: string;
  message: string;
  deepLink: string;
  targetApp: 'cam' | 'gm';
  readAt: string | null;
  gmReadAt: string | null;
  createdAt: string;
};

type ListResponse<T> = {
  data?: {
    items?: T[];
    nextCursor?: string | null;
    hasMore?: boolean;
  };
};

type ItemResponse<T> = { data?: T };

export type Page<T> = { items: T[]; nextCursor: string | null; hasMore: boolean };

function page<T>(body: ListResponse<T>): Page<T> {
  return {
    items: body?.data?.items ?? [],
    nextCursor: body?.data?.nextCursor ?? null,
    hasMore: Boolean(body?.data?.hasMore),
  };
}

// HEADER-DOCS-NOTES-V2: author filter for GET api/cam-mirror/notes.
// Students only receive their own notes. Counselors never receive student notes,
// even when author is "student" or "all".
export type NoteAuthorFilter = 'counselor' | 'mine' | 'student' | 'all';

export const notesApi = {
  async list(studentId: string | number, cursor?: string | null, author?: NoteAuthorFilter): Promise<Page<NoteDto>> {
    const body = (await http.get('api/cam-mirror/notes', {
      params: { studentId, limit: 50, cursor: cursor || undefined, author: author || undefined },
    })) as ListResponse<NoteDto>;
    return page(body);
  },
  async create(studentId: string | number, content: string): Promise<NoteDto> {
    const body = (await http.post('api/cam-mirror/notes', { studentId: Number(studentId), content })) as ItemResponse<NoteDto>;
    return body.data as NoteDto;
  },
  async update(externalId: string, content: string, expectedUpdatedAt: string): Promise<NoteDto> {
    const body = (await http.patch(`api/cam-mirror/notes/${externalId}`, {
      content,
      expectedUpdatedAt,
    })) as ItemResponse<NoteDto>;
    return body.data as NoteDto;
  },
  async remove(externalId: string): Promise<void> {
    await http.delete(`api/cam-mirror/notes/${externalId}`);
  },
};

export const todosApi = {
  async list(
    studentId: string | number,
    scope?: 'counselor' | 'student',
    cursor?: string | null,
  ): Promise<Page<TodoDto>> {
    const body = (await http.get('api/cam-mirror/todos', {
      params: { studentId, scope, limit: 50, cursor: cursor || undefined },
    })) as ListResponse<TodoDto>;
    return page(body);
  },
  async create(studentId: string | number, assignedToRole: 'counselor' | 'student', description: string): Promise<TodoDto> {
    const body = (await http.post('api/cam-mirror/todos', {
      studentId: Number(studentId),
      assignedToRole,
      description,
    })) as ItemResponse<TodoDto>;
    return body.data as TodoDto;
  },
  async update(
    externalId: string,
    patch: { description?: string; isCompleted?: boolean; expectedUpdatedAt?: string },
  ): Promise<TodoDto> {
    const body = (await http.patch(`api/cam-mirror/todos/${externalId}`, patch)) as ItemResponse<TodoDto>;
    return body.data as TodoDto;
  },
  async remove(externalId: string): Promise<void> {
    await http.delete(`api/cam-mirror/todos/${externalId}`);
  },
  async markRead(studentId: string | number): Promise<void> {
    await http.patch('api/cam-mirror/todos/mark-read', {}, { params: { studentId } });
  },
};

export const headerApi = {
  async isEnabled(studentId: string | number): Promise<boolean> {
    const body = (await http.get('api/cam-mirror/header-v2', {
      params: { studentId },
    })) as ItemResponse<{ headerV2?: boolean }>;
    return Boolean(body.data?.headerV2);
  },
};

export const flagsApi = {
  async submit(payload: {
    issueType: string;
    message: string;
    context?: {
      pageUrl?: string;
      studentId?: number;
      userRole?: string;
      schoolName?: string;
    };
  }): Promise<{ success: boolean; clickupMessageId: string | null }> {
    const body = (await http.post('api/cam-mirror/flags', payload)) as ItemResponse<{
      success: boolean;
      clickupMessageId: string | null;
    }>;
    return body.data ?? { success: true, clickupMessageId: null };
  },
};

export const notificationsApi = {
  async list(cursor?: string | null): Promise<Page<NotificationDto>> {
    const body = (await http.get('api/cam-mirror/notifications', {
      params: { limit: 50, ...(cursor ? { cursor } : {}) },
    })) as ListResponse<NotificationDto>;
    return page(body);
  },
  async markRead(id: number): Promise<void> {
    await http.patch(`api/cam-mirror/notifications/${id}/read`);
  },
  async markAllRead(): Promise<void> {
    await http.patch('api/cam-mirror/notifications/mark-all-read');
  },
  async resolve(id: number): Promise<{ status: 'ok' | 'target_deleted' | 'other_app'; deepLink: string | null; targetApp: 'cam' | 'gm' | null }> {
    const body = (await http.get(`api/cam-mirror/notifications/${id}/resolve`)) as ItemResponse<{
      status: 'ok' | 'target_deleted' | 'other_app';
      deepLink: string | null;
      targetApp: 'cam' | 'gm' | null;
    }>;
    return body.data ?? { status: 'target_deleted', deepLink: null, targetApp: null };
  },
};

// -----------------------------------------------------------------------------
// HEADER-DOCS-012: documentsApi (backend HEADER-DOCS-005, cam-documents.controller.ts)
// Merged on top of the notes/todos/header/flags/notifications client above.
// The shared `http` interceptor resolves with the response body, i.e. the
// `{ data: T, meta }` envelope, so each call unwraps `.data` once.
// -----------------------------------------------------------------------------

export type DocType =
  | 'transcript'
  | 'resume'
  | 'test_score'
  | 'essay_draft'
  | 'fee_waiver'
  | 'school_form'
  | 'other';

export const DOC_TYPES: DocType[] = [
  'transcript',
  'resume',
  'test_score',
  'essay_draft',
  'fee_waiver',
  'school_form',
  'other',
];

export const DOC_TYPE_LABELS: Record<DocType, string> = {
  transcript: 'Transcript',
  resume: 'Resume',
  test_score: 'Test score',
  essay_draft: 'Essay draft',
  fee_waiver: 'Fee waiver',
  school_form: 'School form',
  other: 'Other',
};

export type DocOrigin = 'direct' | 'chat' | 'link';

export type DocumentAttachmentDto = {
  externalId: string;
  mimeType: string;
  sizeBytes: number;
  downloadUrl: string | null; // presigned, 5-min TTL; null on S3 hiccup at list time
};

export type DocumentVisibilityDto = {
  student: boolean;
  parent: boolean;
  counselor: boolean; // server-forced true in v1 (§15.1 / §19.C)
};

export type DocumentDto = {
  externalId: string;
  studentId: number;
  displayName: string;
  docType: DocType;
  origin: DocOrigin;
  linkUrl: string | null;
  linkSourceLabel: string | null;
  attachment: DocumentAttachmentDto | null;
  visibility: DocumentVisibilityDto;
  createdBy: { id: number; role: string; name: string | null };
  createdAt: string;
  updatedAt: string; // send back as expectedUpdatedAt on PATCH
};

export type InitiateUploadResponse = {
  uploadUrl: string;
  attachmentExternalId: string;
  uploadExpiresAt: string;
  // HEADER-DOCS-026 follow-up: Content-Type the PUT URL was signed with (optional for older backends)
  contentType?: string;
};

export type DownloadUrlResponse = { downloadUrl: string; expiresAt: string };

async function unwrapData<T>(promise: Promise<unknown>): Promise<T> {
  const body = (await promise) as ItemResponse<T>;
  return body.data as T;
}

function newIdempotencyKey(): string {
  // Server dedupes POSTs by (userId, endpoint, key). randomUUID is cast: TS 4.1 dom lib lacks it.
  const c: any = typeof crypto !== 'undefined' ? crypto : undefined;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export const documentsApi = {
  async list(params: {
    studentId: number;
    docType?: DocType;
    origin?: DocOrigin;
    limit?: number;
    cursor?: string;
    // HEADER-DOCS-026: chat Files tab, e.g. "session_200-100". Backend forces origin=chat
    // and returns 403 for parents or callers who are not on the thread.
    conversationId?: string;
  }): Promise<Page<DocumentDto>> {
    const body = (await http.get('api/cam-mirror/documents', { params })) as ListResponse<DocumentDto>;
    return page(body);
  },

  initiateUpload: (body: {
    studentId: number;
    filename: string;
    mimeType: string;
    sizeBytes: number;
    docType: DocType;
  }) => unwrapData<InitiateUploadResponse>(http.post('api/cam-mirror/documents/upload/initiate', body)),

  cancelUpload: (attachmentExternalId: string) =>
    unwrapData<{ success: true }>(http.delete(`api/cam-mirror/documents/upload/${attachmentExternalId}`)),

  create: (body: {
    studentId: number;
    attachmentExternalId?: string;
    linkUrl?: string;
    displayName: string;
    docType: DocType;
    visibility?: Partial<DocumentVisibilityDto>;
  }) =>
    unwrapData<DocumentDto>(
      http.post('api/cam-mirror/documents', body, {
        headers: { 'Idempotency-Key': newIdempotencyKey() },
      }),
    ),

  getDownloadUrl: (externalId: string) =>
    unwrapData<DownloadUrlResponse>(http.get(`api/cam-mirror/documents/${externalId}/download-url`)),

  update: (
    externalId: string,
    body: {
      displayName?: string;
      docType?: DocType;
      visibility?: Partial<DocumentVisibilityDto>;
      expectedUpdatedAt: string;
    },
  ) => unwrapData<DocumentDto>(http.patch(`api/cam-mirror/documents/${externalId}`, body)),

  softDelete: (externalId: string) =>
    unwrapData<{ success: true }>(http.delete(`api/cam-mirror/documents/${externalId}`)),

  // Direct-to-S3 PUT between initiateUpload() and create(). Plain axios on purpose:
  // a presigned URL must not get our baseURL or Authorization header.
  // contentType: the normalized MIME returned by initiateUpload. The PUT URL is signed with it,
  // so sending the raw browser value (e.g. '' for HEIC, vnd.ms-excel for .csv) fails with
  // SignatureDoesNotMatch. Falls back to file.type for older backends that do not return it.
  async uploadToS3(
    uploadUrl: string,
    file: File,
    onProgress?: (percent: number) => void,
    contentType?: string,
  ): Promise<void> {
    await axios.put(uploadUrl, file, {
      headers: { 'Content-Type': contentType || file.type || 'application/octet-stream' },
      onUploadProgress: onProgress
        ? (evt: ProgressEvent) => {
            if (evt.total) onProgress(Math.round((evt.loaded / evt.total) * 100));
          }
        : undefined,
    });
  },
};

export type SmsParticipantDto = {
  userId: number;
  role: string;
  phoneNumber: string;
  phoneVerified: boolean;
  smsReceiveEnabled: boolean;
  plan: 'free' | 'starter' | 'paid';
  smsAllowed: boolean;
};

export const smsApi = {
  async getParticipant(userId: string | number): Promise<SmsParticipantDto> {
    const body = (await http.get(`api/cam-mirror/chat/participant/${userId}`)) as ItemResponse<SmsParticipantDto>;
    return body.data as SmsParticipantDto;
  },
  async setPreference(enabled: boolean): Promise<SmsParticipantDto> {
    const body = (await http.patch('api/cam-mirror/user/settings/sms-preference', { enabled })) as ItemResponse<SmsParticipantDto>;
    return body.data as SmsParticipantDto;
  },
};

// Typed error body for FE branching (Nest exceptions thrown with `{ code, ... }`).
export type ApiErrorBody = {
  code: string; // stable machine code, e.g. stale_document, link_already_added
  detail?: string;
  currentDocument?: DocumentDto; // 409 stale_document
  existingDocumentExternalId?: string | null; // 409 link_already_added
  retryAfter?: number; // 503 s3_unavailable
};

export function isApiError(err: unknown): err is { response: { data: ApiErrorBody; status: number } } {
  return typeof err === 'object' && err != null && typeof (err as any).response?.data?.code === 'string';
}

// HTTP status of any axios error (also for bodies without a `code`, e.g. throttler 429).
export function httpStatusOf(err: unknown): number | undefined {
  return (err as any)?.response?.status;
}
